 
import React, { useState, useEffect } from 'react';
import Joi from 'joi';
import classNames from 'classnames';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { Form } from 'react-bootstrap';
import LoadingSpinner from '../../../components/spinner/LoadingSpinner';
import WithOnlyOnDesktop from '../../../components/onlyOnDesktop/withOnlyOnDesktop';
import Select from '../../../components/form/Select';
import ProfessionalsSubtype from '../../../components/professionals/ProfessionalsSubtype';
import { mapCaregiversCertifications, getProfessionalCityState } from '../../../utils/ProfessionalsUtils';
import EmployeeIdsSection from './EmployeeIdsSection';
import { PencilIcon, InfoIcon } from '../../../components/icons';
import { user } from '../../../redux/user';
import { leaveConfirmationCopy } from '../../../redux/navigate';
import { showNotification } from '../../../redux/notification';
import ConfirmationModalNew from '../../../components/modals/ConfirmationModalNew';
import { useUpdateFlexProfessionalMutation } from '../../../redux/managementCompanyWorkforce';
import { useDropdownsQuery } from '../../../redux/dropdowns';
import utils from '../../../utils/utils';
import PERMISSION from '../../../constants/Permissions';

import '../../../components/professionals/ProfessionalsEmployeeDetails.scss';
import '../../../components/professionals/ProfessionalsEditEmployee.scss';

const validationSchema = Joi.object({
	first_name: Joi.string()
		.max(50)
		.trim()
		.pattern(/^[a-zA-Z -]+$/)
		.required()
		.messages({
			'string.empty': 'First Name is required',
			'any.required': 'First Name is required',
			'string.max': 'First Name can have a maximum length of 50 characters',
			'string.pattern.base': 'First Name is not valid'
		}),
	last_name: Joi.string()
		.max(50)
		.trim()
		.pattern(/^[a-zA-Z -]+$/)
		.required()
		.messages({
			'string.empty': 'Last Name is required',
			'any.required': 'Last Name is required',
			'string.max': 'Last Name can have a maximum length of 50 characters',
			'string.pattern.base': 'Last Name is not valid'
		}),
	email: Joi.string()
		.max(129)
		.trim()
		.email({ tlds: { allow: false } })
		.required()
		.messages({
			'string.empty': 'Email is required',
			'any.required': 'Email is required',
			'string.email': 'Email format is invalid',
			'string.max': 'Email can have a maximum length of 129 characters'
		}),
	zip_code: Joi.string()
		.length(5)
		.pattern(/^[0-9]{5}$/)
		.required()
		.messages({
			'string.empty': 'Zip Code is required',
			'any.required': 'Zip Code is required',
			'string.length': 'Zip Code is not valid',
			'string.pattern.base': 'Zip code is not valid'
		}),
	address: Joi.string().max(255).trim().allow('').optional(),
	city_name: Joi.string().trim().allow('').optional(),
	state_code: Joi.string().allow('').optional()
});

const validateProfileFields = (data) => {
	const { error } = validationSchema.validate(data, { abortEarly: false });
	const formErrors = error
		? error.details.reduce((acc, curr) => {
				acc[curr.context.key] = curr.message;
				return acc;
		  }, {})
		: {};
	return formErrors;
};

const buildProfileFields = (professional) => ({
	first_name: (professional && (professional.raw_first_name || professional.first_name)) || '',
	last_name: (professional && (professional.raw_last_name || professional.last_name)) || '',
	email: (professional && professional.email) || '',
	address: (professional && professional.address) || '',
	city_name: (professional && professional.city_name) || '',
	state_code: (professional && professional.state_code) || '',
	zip_code: (professional && professional.zip_code) || ''
});

const FlexProfessionalInformation = ({
	professional,
	isFetching,
	managementCompanyId,
	caregiverId,
	onEditModeChange,
	onDirtyChange
}) => {
	const dispatch = useDispatch();
	const userData = useSelector(user, shallowEqual);
	const copy = useSelector(leaveConfirmationCopy, shallowEqual);
	const { data: states } = useDropdownsQuery('states');

	const [isEditMode, setIsEditMode] = useState(false);
	const [profileFields, setProfileFields] = useState(buildProfileFields(professional));
	const [profileErrors, setProfileErrors] = useState({});
	const [employeeIdsData, setEmployeeIdsData] = useState([]);
	const [employeeIdsErrors, setEmployeeIdsErrors] = useState([]);
	const [isSaving, setIsSaving] = useState(false);
	const [isDirty, setIsDirty] = useState(false);
	const [showLeaveConfirmation, setShowLeaveConfirmation] = useState(false);

	const [updateFlexProfessional] = useUpdateFlexProfessionalMutation();

	const canEditProfile = utils.isAuthorizedExact(
		PERMISSION.PORTAL_EDIT_FLEX_PROFESSIONAL,
		userData.managementCompanyPermissions[managementCompanyId]
	);
	const canEditEmployeeIds = utils.isAuthorizedExact(
		PERMISSION.EMPLOYEE_IDS_EDIT,
		userData.managementCompanyPermissions[managementCompanyId]
	);
	const canViewCredentialsBanner =
		utils.isAuthorizedExact(
			PERMISSION.WORKFORCE_CREDENTIALS_VIEW,
			userData.managementCompanyPermissions[managementCompanyId]
		) &&
		utils.isAuthorizedExact(
			PERMISSION.WORKFORCE_CREDENTIALS_EDIT,
			userData.managementCompanyPermissions[managementCompanyId]
		);

	useEffect(() => {
		if (onEditModeChange) {
			onEditModeChange(isEditMode);
		}
	}, [isEditMode, onEditModeChange]);

	useEffect(() => {
		if (onDirtyChange) {
			onDirtyChange(isDirty);
		}
	}, [isDirty, onDirtyChange]);

	if (isFetching) {
		return (
			<div className="spinner-center-wrapper" key="spinner">
				<LoadingSpinner className="saving-spinner" />
			</div>
		);
	}

	if (!professional) {
		return null;
	}

	const professionalCertifications = mapCaregiversCertifications(professional);
	const hasEmployeeIdsToEdit = (professional.employee_ids || []).length > 0;

	const enterEditMode = () => {
		setProfileFields(buildProfileFields(professional));
		setEmployeeIdsData([...(professional.employee_ids || [])]);
		setProfileErrors({});
		setEmployeeIdsErrors([]);
		setIsEditMode(true);
	};

	const exitEditMode = () => {
		setIsEditMode(false);
		setProfileFields(buildProfileFields(professional));
		setEmployeeIdsData([...(professional.employee_ids || [])]);
		setProfileErrors({});
		setEmployeeIdsErrors([]);
		setIsDirty(false);
	};

	const handleCancelClick = () => {
		if (isDirty) {
			setShowLeaveConfirmation(true);
			return;
		}
		exitEditMode();
	};

	const handleConfirmLeave = () => {
		setShowLeaveConfirmation(false);
		exitEditMode();
	};

	const handleFieldChange = (e) => {
		const { name, value } = e.target;
		setProfileFields({ ...profileFields, [name]: value });
		setIsDirty(true);
	};

	const handleSelectChange = (value) => {
		setProfileFields({ ...profileFields, ...value });
		setIsDirty(true);
	};

	const handleEmployeeIdChange = (index, value) => {
		const updated = [...employeeIdsData];
		updated[index] = { ...updated[index], value };
		setEmployeeIdsData(updated);
		setIsDirty(true);
	};

	const performSave = async () => {
		setIsSaving(true);

		const trimmedEmployeeIds = employeeIdsData.map((item) => ({
			...item,
			value: item.value ? item.value.trim() || null : null
		}));

		try {
			if (canEditProfile || canEditEmployeeIds) {
				await updateFlexProfessional({
					managementCompanyId,
					caregiverId,
					body: {
						...(canEditProfile ? profileFields : {}),
						...(canEditEmployeeIds ? { employee_ids: trimmedEmployeeIds } : {})
					}
				}).unwrap();
			}

			setProfileErrors({});
			setEmployeeIdsErrors([]);
			setIsDirty(false);
			dispatch(showNotification('success', 'Professional information saved successfully.'));
			setIsEditMode(false);
		} catch (error) {
			const errorData = error && error.data;
			const errors = errorData && errorData.validationError ? errorData.errors : [];

			if (canEditProfile) {
				const profileFieldErrors = {};
				const employeeIdErrors = [];
				errors.forEach((err) => {
					if (err.typeId !== undefined) {
						employeeIdErrors.push(err);
					} else if (err.field) {
						profileFieldErrors[err.field] = err.message;
					}
				});
				setProfileErrors(profileFieldErrors);
				setEmployeeIdsErrors(employeeIdErrors);
			} else {
				setEmployeeIdsErrors(errors);
			}

			dispatch(showNotification('error', 'Changes could not be saved. Please verify your changes and try again.'));
		}

		setIsSaving(false);
	};

	const handleSaveClick = () => {
		const errors = canEditProfile ? validateProfileFields(profileFields) : {};
		setProfileErrors(errors);
		if (Object.keys(errors).length > 0) {
			return;
		}
		performSave();
	};

	const renderProfessionalStatusReadOnly = () => (
		<div className="details">
			<h3>Professional status</h3>
			<h4>
				{professional.employee_status
					? professional.employee_status.charAt(0) + professional.employee_status.slice(1).toLowerCase()
					: '-'}
			</h4>
		</div>
	);

	const renderProfileFieldsReadOnly = () => (
		<>
			<div className="details__wrapper">
				<div className="details">
					<h3>Name</h3>
					<h4>
						{professional.first_name || '-'} {professional.last_name || '-'}
					</h4>
				</div>
				{renderProfessionalStatusReadOnly()}
			</div>
			<div className="home-details__wrapper">
				{professionalCertifications.map((certif) => (
					<div className="home-details-license__wrapper" key={certif.resource_type_id || 'no-cert'}>
						<div className="home-details license-type">
							<h3>License Type</h3>
							<h4>{certif.resource_type}</h4>
						</div>
						<ProfessionalsSubtype subtypeTitle="State" subtypes={certif.states} type="code" />
						<ProfessionalsSubtype subtypeTitle="Specialties" subtypes={certif.specialties} type="label" />
					</div>
				))}
				{canViewCredentialsBanner && (
					<div className="credentials-info-banner">
						<InfoIcon />
						<span>You can edit this anytime on the Credentials tab</span>
					</div>
				)}
			</div>
		</>
	);

	const renderHomeAddressReadOnly = () => (
		<div className="name-and-licenses__wrapper">
			<h2>Home Address</h2>
			<div className="home-details__wrapper">
				<div className="home-details">
					<h3>Street Address</h3>
					<h4>{professional.address || '-'}</h4>
				</div>
				<div className="home-details">
					<h3>City, State</h3>
					{getProfessionalCityState(professional)}
				</div>
				<div className="home-details">
					<h3>ZIP</h3>
					<h4>{professional.zip_code || '-'}</h4>
				</div>
			</div>
		</div>
	);

	const renderContactInfoReadOnly = () => (
		<div className="name-and-licenses__wrapper">
			<h2>Contact Information</h2>
			<div className="details__wrapper details">
				<h3>Cell Phone</h3>
				<h4>{professional.phone_number || '-'}</h4>
			</div>
			<div className="details-email__wrapper">
				<h3>Email</h3>
				<h4>{professional.email || '-'}</h4>
			</div>
		</div>
	);

	const renderEmployeeIdsRows = () => {
		const employeeIds = professional.employee_ids || [];
		if (employeeIds.length === 0) return null;

		return (
			<div className="home-details__wrapper professional-info-section">
				{employeeIds.map((item) => (
					<div key={item.type_id} className="home-details">
						<h3>
							{item.name} {item.is_primary && <strong>(primary ID)</strong>}
						</h3>
						<h4>{item.value || '-'}</h4>
					</div>
				))}
			</div>
		);
	};

	const readOnlyView = () => (
		<div className="name-and-licenses__wrapper">
			<div className="d-flex justify-content-between professional-info-header">
				<h2>Professional Information</h2>
				{(canEditProfile || (canEditEmployeeIds && hasEmployeeIdsToEdit)) && (
					<button type="button" className="edit-button" onClick={enterEditMode}>
						<PencilIcon color="#7200A0" width={12} height={12} />
						Edit
					</button>
				)}
			</div>
			<div className="professional-info-section">{renderProfileFieldsReadOnly()}</div>
			{renderEmployeeIdsRows()}
			<div className="home-details__wrapper professional-info-section">
				<div className="home-details">
					<h3>Street address</h3>
					<h4>{professional.address || '-'}</h4>
				</div>
				<div className="home-details">
					<h3>City, State</h3>
					{getProfessionalCityState(professional)}
				</div>
				<div className="home-details">
					<h3>ZIP</h3>
					<h4>{professional.zip_code || '-'}</h4>
				</div>
			</div>
			<div className="details__wrapper details">
				<h3>Cell phone</h3>
				<h4>{professional.phone_number || '-'}</h4>
			</div>
			<div className="details-email__wrapper">
				<h3>Email</h3>
				<h4>{professional.email || '-'}</h4>
			</div>
		</div>
	);

	const editView = () => (
		<>
			<div className="professional-info-header">
				<h2>Professional Information</h2>
			</div>

			{canEditProfile ? (
				<>
					<div className="employee-details">
						<h2>Name</h2>
						<Form.Group controlId="first_name" className="form-input-group">
							<Form.Label>First name</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="first_name"
								placeholder="First name"
								value={profileFields.first_name}
								onChange={handleFieldChange}
								isInvalid={!!profileErrors.first_name}
							/>
							<Form.Control.Feedback type="invalid">{profileErrors.first_name}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="last_name" className="form-input-group">
							<Form.Label>Last name</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="last_name"
								placeholder="Last name"
								value={profileFields.last_name}
								onChange={handleFieldChange}
								isInvalid={!!profileErrors.last_name}
							/>
							<Form.Control.Feedback type="invalid">{profileErrors.last_name}</Form.Control.Feedback>
						</Form.Group>
						{renderProfessionalStatusReadOnly()}
					</div>

					<div className="employee-details">
						<h2>Licenses</h2>
						<div className="home-details__wrapper">
							{professionalCertifications.map((certif) => (
								<div className="home-details-license__wrapper" key={certif.resource_type_id || 'no-cert'}>
									<div className="home-details license-type">
										<h3>License Type</h3>
										<h4>{certif.resource_type}</h4>
									</div>
									<ProfessionalsSubtype subtypeTitle="State" subtypes={certif.states} type="code" />
									<ProfessionalsSubtype subtypeTitle="Specialties" subtypes={certif.specialties} type="label" />
								</div>
							))}
							{canViewCredentialsBanner && (
								<div className="credentials-info-banner">
									<InfoIcon />
									<span>You can edit this anytime on the Credentials tab</span>
								</div>
							)}
						</div>
					</div>
				</>
			) : (
				<div className="name-and-licenses__wrapper">{renderProfileFieldsReadOnly()}</div>
			)}

			{canEditEmployeeIds ? (
				<EmployeeIdsSection
					data={employeeIdsData}
					isEditMode
					validationErrors={employeeIdsErrors}
					onChange={handleEmployeeIdChange}
				/>
			) : (
				<EmployeeIdsSection
					data={professional.employee_ids || []}
					isEditMode={false}
					validationErrors={[]}
					onChange={() => {}}
				/>
			)}

			{canEditProfile ? (
				<div className="employee-details">
					<h2>Home Address</h2>
					<Form.Group controlId="address" className="form-input-group">
						<Form.Label>
							Street address <span className="optional-label">(Optional)</span>
						</Form.Label>
						<Form.Control
							size="lg"
							type="text"
							className="form-input"
							name="address"
							placeholder="Street address"
							value={profileFields.address}
							onChange={handleFieldChange}
							isInvalid={!!profileErrors.address}
						/>
						<Form.Control.Feedback type="invalid">{profileErrors.address}</Form.Control.Feedback>
					</Form.Group>
					<div className="input-group">
						<Form.Group controlId="city_name" className="form-input-group location-input-group">
							<Form.Label>
								City <span className="optional-label">(Optional)</span>
							</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="city_name"
								placeholder="City"
								value={profileFields.city_name}
								onChange={handleFieldChange}
								isInvalid={!!profileErrors.city_name}
							/>
							<Form.Control.Feedback type="invalid">{profileErrors.city_name}</Form.Control.Feedback>
						</Form.Group>
						<Form.Group controlId="state_code" className="form-input-group location-input-group">
							<Form.Label className="form-label">
								State <span className="optional-label">(Optional)</span>
							</Form.Label>
							<Select
								className="select employee-edit-page"
								name="state_code"
								options={states}
								value={profileFields.state_code}
								handleChange={handleSelectChange}
								placeholder="Select..."
								isSearchable
							/>
							{profileErrors.state_code && <div className="invalid-feedback-custom">{profileErrors.state_code}</div>}
						</Form.Group>
						<Form.Group controlId="zip_code" className="form-input-group location-input-group">
							<Form.Label>ZIP</Form.Label>
							<Form.Control
								size="lg"
								type="text"
								className="form-input"
								name="zip_code"
								placeholder="ZIP"
								value={profileFields.zip_code}
								onChange={handleFieldChange}
								isInvalid={!!profileErrors.zip_code}
							/>
							<Form.Control.Feedback type="invalid">{profileErrors.zip_code}</Form.Control.Feedback>
						</Form.Group>
					</div>
				</div>
			) : (
				renderHomeAddressReadOnly()
			)}

			{canEditProfile ? (
				<div className="employee-details">
					<h2>Contact Information</h2>
					<Form.Group controlId="phone" className="form-input-group">
						<Form.Label>Cell phone</Form.Label>
						<Form.Control
							size="lg"
							type="text"
							className="form-input"
							value={professional.phone_number || ''}
							disabled
							readOnly
						/>
					</Form.Group>
					<Form.Group controlId="email" className="form-input-group">
						<Form.Label>Email</Form.Label>
						<Form.Control
							size="lg"
							type="text"
							className="form-input"
							name="email"
							placeholder="email@address.com"
							value={profileFields.email}
							onChange={handleFieldChange}
							isInvalid={!!profileErrors.email}
						/>
						<Form.Control.Feedback type="invalid">{profileErrors.email}</Form.Control.Feedback>
					</Form.Group>
				</div>
			) : (
				renderContactInfoReadOnly()
			)}

			<div className="form-actions">
				<button type="button" className="form-actions__button secondary-button" onClick={handleCancelClick}>
					Cancel
				</button>
				<button
					type="button"
					className="form-actions__button primary-button"
					disabled={isSaving}
					onClick={handleSaveClick}
				>
					Save changes
				</button>
			</div>
			{showLeaveConfirmation && (
				<ConfirmationModalNew
					title={copy.title}
					confirmLabel={copy.confirmLabel}
					cancelLabel={copy.cancelLabel}
					onConfirm={handleConfirmLeave}
					onCancel={() => setShowLeaveConfirmation(false)}
				>
					{copy.message}
				</ConfirmationModalNew>
			)}
		</>
	);

	return (
		<WithOnlyOnDesktop>
			<div
				className={classNames('employee-details__wrapper', {
					'flex-workforce edit-professional-details': isEditMode
				})}
			>
				{isEditMode ? editView() : readOnlyView()}
			</div>
		</WithOnlyOnDesktop>
	);
};

export default FlexProfessionalInformation;
